import { TitleCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IconComponent } from '@examdojo/core/icon';
import { IconButtonComponent } from '@examdojo/core/icon-button';
import {
  getProcedureCategoryColor,
  Procedure,
  PROCEDURE_CATEGORIES,
  ProcedureCategory,
  Representation,
  Tag,
} from '@examdojo/core/ontology';
import { MarkdownModule } from 'ngx-markdown';
import { ProcedureDetailsComponent } from './nodes/procedure-details/procedure-details.component';
import { RepresentationDetailsComponent } from './nodes/representation-details/representation-details.component';
import { TagDetailsComponent } from './nodes/tag-details/tag-details.component';

export type TagNode = Tag & { key: string; label?: string };
export type ProcedureNode = Procedure & { key: string; label?: string; procedureCategory?: Procedure['category'] };
export type RepresentationNode = Representation & { key: string; label?: string };

@Component({
  selector: 'exam-node-details',
  standalone: true,
  imports: [
    IconButtonComponent,
    IconComponent,
    TagDetailsComponent,
    RepresentationDetailsComponent,
    ProcedureDetailsComponent,
    TitleCasePipe,
    MatTooltipModule,
    MatMenuModule,
    MarkdownModule,
  ],
  templateUrl: './node-details.component.html',
  styleUrls: ['./node-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'h-full block' },
})
export class NodeDetailsComponent {
  readonly katexOptions = {
    throwOnError: false,
    displayMode: false,
    strict: false,
  };

  readonly procedureCategories = PROCEDURE_CATEGORIES;

  readonly node = input<Record<string, unknown> | null>(null);
  readonly sourceProcedure = input<Procedure | null>(null);
  readonly sourceProcedures = input<Procedure[]>([]);
  readonly rootRepresentationIds = input<Set<string>>(new Set());
  readonly runtimeState = input<Record<string, unknown>>({});
  readonly representationsMap = input<Map<string, Representation>>(new Map());
  readonly tagsMap = input<Map<string, Tag>>(new Map());

  readonly closePanel = output<void>();
  readonly navigateToNode = output<string>();
  readonly executeRoot = output<{ id: string; values: unknown }>();
  readonly autoExecuteRoot = output<void>();

  readonly nodeType = computed(() => {
    const n = this.node();
    return n ? (n['nodeType'] as string) : null;
  });

  readonly tagNode = computed<TagNode | null>(() => {
    const n = this.node();
    if (!n || this.nodeType() !== 'tag') {
      return null;
    }
    return n as unknown as TagNode;
  });

  readonly representationNode = computed<RepresentationNode | null>(() => {
    const n = this.node();
    if (!n || this.nodeType() !== 'representation') {
      return null;
    }
    return n as unknown as RepresentationNode;
  });

  readonly procedureNode = computed<ProcedureNode | null>(() => {
    const n = this.node();
    if (!n || this.nodeType() !== 'procedure') {
      return null;
    }
    return n as unknown as ProcedureNode;
  });

  readonly displayTitle = computed(() => {
    const n = this.node();
    if (!n) {
      return '';
    }

    const type = this.nodeType();

    if (type === 'tag') {
      const t = this.tagNode();
      return t?.label ?? t?.name ?? '';
    }

    if (type === 'procedure') {
      const p = this.procedureNode();
      return p?.label ?? p?.name ?? p?.key ?? '';
    }

    if (type === 'representation') {
      const r = this.representationNode();
      return r?.label ?? r?.name ?? r?.key ?? '';
    }

    const fallback = n as { name?: string; label?: string; key?: string; display_name?: string };
    return fallback.display_name ?? fallback.label ?? fallback.name ?? fallback.key ?? '';
  });

  readonly displayType = computed(() => {
    const type = this.nodeType();
    if (!type) {
      return '';
    }
    return type === 'tag' ? 'topic' : type;
  });

  readonly badgeColor = computed(() => {
    switch (this.displayType()) {
      case 'topic':
        return '#c7d2fe';
      case 'representation':
        return '#e5e7eb';
      case 'procedure':
        return '#e9d5ff';
      default:
        return '#f3f4f6';
    }
  });

  readonly procedureCategoryLabel = computed(() => {
    const p = this.procedureNode();
    if (!p) {
      return '';
    }
    return p.procedureCategory ?? p.category ?? '';
  });

  readonly procedureCategoryColor = computed(() => {
    const category = this.procedureCategoryLabel() as ProcedureCategory;
    if (!category) {
      return '';
    }
    return getProcedureCategoryColor(category);
  });

  readonly isRoot = computed(() => {
    const repr = this.representationNode();
    if (!repr) {
      return false;
    }
    return this.rootRepresentationIds().has(repr.id);
  });

  readonly runtimeValue = computed<Record<string, unknown> | null>(() => {
    const repr = this.representationNode();
    if (!repr) {
      return null;
    }
    const val = this.runtimeState()[repr.id];
    if (val && typeof val === 'object') {
      return val as Record<string, unknown>;
    }
    return null;
  });

  onExecute(values: unknown): void {
    const repr = this.representationNode();
    if (repr) {
      this.executeRoot.emit({ id: repr.id, values });
    }
  }
}
